<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import EditorCanvas from './components/EditorCanvas.vue';
import EditorComponentPanel from './components/EditorComponentPanel.vue';
import EditorPropertiesPanel from './components/EditorPropertiesPanel.vue';
import EditorCodePanel from './components/EditorCodePanel.vue';
import EditorPreviewPane from './components/EditorPreviewPane.vue';
import EditorStylesPanel from './components/EditorStylesPanel.vue';
import EditorLayersPanel from './components/EditorLayersPanel.vue';
import EditorEventSelector from './components/EditorEventSelector.vue';
import EditorPortalSettingsPanel from './components/EditorPortalSettingsPanel.vue';
import { useEditorHistory } from './composables/useEditorHistory';
import {
  parse,
  serialize,
  parsePages,
  serializePages,
  SYSTEM_STAGES,
  DEFAULT_PAGE_ID,
  nextPageId,
  findNode,
  insertNode,
  removeNode,
  moveNode,
  makeBlock,
  resolveInsertTarget,
  applyColumnCount,
  applyColumnGap,
  isLayoutInsertTag,
  duplicateNode,
} from './lib/documentModel';
import {
  PORTAL_PREVIEW_STUB_ID,
  PORTAL_PREVIEW_EVENT_STUB,
  isPortalPreviewStub,
} from './lib/previewEventStub';
import { eventAttributePickerOptions } from './lib/eventAttributePickerOptions';
import { DEFAULT_THEME } from './lib/portalTheme';
import { capturePortalHtmlAsPng } from './lib/capturePortalPreview';

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { pushState, undo, redo, canUndo, canRedo, pause, resume } =
  useEditorHistory();

const templateId = computed(() => route.params.portalId);
const template = computed(
  () =>
    store.getters['planningEventPortals/getTemplateById'](templateId.value) ||
    {}
);
const components = computed(
  () => store.getters['planningEventPageComponents/getRecords'] || []
);

const saving = ref(false);
const openingPreview = ref(false);
const document = ref('');
const pages = ref([{ id: DEFAULT_PAGE_ID, systemStage: null, nodes: [] }]);
const activePageId = ref(DEFAULT_PAGE_ID);
const nodes = ref([]);
const selectedId = ref(null);
const theme = ref({ ...DEFAULT_THEME });
const name = ref('');
const acceptanceMode = ref('');
const portalEditableAttributeKeys = ref([]);
const showCode = ref(false);
const deviceMode = ref('desktop');
const selectedEventId = ref(PORTAL_PREVIEW_STUB_ID);
const selectedEvent = ref(PORTAL_PREVIEW_EVENT_STUB);
const leftTab = ref('elements'); // elements | structure | settings
const rightTab = ref('properties'); // properties | theme
const feedbackEnabled = ref(false);
const inboxId = ref('');
const coverPreviewSource = ref('');
const coverPreviewBgColor = ref('');
const thumbnailUrl = ref('');
/** Bumps on in-place leaf edits so serializeForEditor recomputes without array clone. */
const nodesRevision = ref(0);

let lastPushed = '';
let syncingFromString = false;
let commitTimer = null;
const COMMIT_DEBOUNCE_MS = 200;

const selectedNode = computed(() => {
  if (!selectedId.value) return null;
  return findNode(nodes.value, selectedId.value)?.node || null;
});

const usedStages = computed(() =>
  pages.value.map(p => p.systemStage).filter(Boolean)
);

const availableStages = computed(() =>
  SYSTEM_STAGES.filter(stage => !usedStages.value.includes(stage))
);

const syncActivePageNodes = () => {
  const page = pages.value.find(p => p.id === activePageId.value);
  if (page) page.nodes = nodes.value;
};

const commitDocument = () => {
  syncActivePageNodes();
  const next = serializePages(pages.value);
  if (next === document.value) return;
  document.value = next;
};

/** Immediate serialize — structural edits, save, undo, code toggle. */
const flushCommitDocument = () => {
  if (commitTimer) {
    clearTimeout(commitTimer);
    commitTimer = null;
  }
  commitDocument();
};

/** Debounced serialize for content/prop typing — coalesces undo + avoids per-keystroke work. */
const scheduleCommitDocument = () => {
  if (commitTimer) clearTimeout(commitTimer);
  commitTimer = setTimeout(() => {
    commitTimer = null;
    commitDocument();
  }, COMMIT_DEBOUNCE_MS);
};

const activatePage = (pageId, { clearSelection = true } = {}) => {
  if (!pageId || pageId === activePageId.value) {
    if (clearSelection) selectedId.value = null;
    return;
  }
  syncActivePageNodes();
  const page = pages.value.find(p => p.id === pageId);
  if (!page) return;
  activePageId.value = page.id;
  nodes.value = page.nodes;
  if (clearSelection) selectedId.value = null;
};

const loadFromString = (str, { clearSelection = true } = {}) => {
  syncingFromString = true;
  const parsed = parsePages(str || '');
  pages.value = parsed;
  const preferred =
    parsed.find(p => p.id === activePageId.value) ||
    parsed.find(p => !p.systemStage) ||
    parsed[0];
  activePageId.value = preferred?.id || DEFAULT_PAGE_ID;
  nodes.value = preferred?.nodes || [];
  document.value = str || '';
  lastPushed = document.value;
  if (clearSelection) selectedId.value = null;
  syncingFromString = false;
};

const addPage = systemStage => {
  if (!systemStage || !availableStages.value.includes(systemStage)) return;
  syncActivePageNodes();
  const defaultPage = pages.value.find(p => !p.systemStage) || pages.value[0];
  const clonedNodes = parse(serialize(defaultPage?.nodes || []));
  const id = nextPageId(systemStage);
  pages.value = [...pages.value, { id, systemStage, nodes: clonedNodes }];
  activePageId.value = id;
  nodes.value = clonedNodes;
  selectedId.value = null;
  flushCommitDocument();
};

const removePage = pageId => {
  const page = pages.value.find(p => p.id === pageId);
  if (!page || !page.systemStage) return;
  syncActivePageNodes();
  pages.value = pages.value.filter(p => p.id !== pageId);
  if (activePageId.value === pageId) {
    const fallback = pages.value.find(p => !p.systemStage) || pages.value[0];
    activePageId.value = fallback.id;
    nodes.value = fallback.nodes;
    selectedId.value = null;
  }
  flushCommitDocument();
};

const reassignPageStage = ({ pageId, systemStage }) => {
  const page = pages.value.find(p => p.id === pageId);
  if (!page || !page.systemStage) return;
  if (!systemStage || !SYSTEM_STAGES.includes(systemStage)) return;
  if (
    usedStages.value.includes(systemStage) &&
    page.systemStage !== systemStage
  ) {
    return;
  }
  page.systemStage = systemStage;
  pages.value = [...pages.value];
  flushCommitDocument();
};

const loadTemplate = () => {
  if (!template.value?.id) return;

  const remoteDoc = template.value.document || '';
  const remoteTheme = { ...DEFAULT_THEME, ...(template.value.theme || {}) };

  // Always sync metadata that does not remount canvas previews
  name.value = template.value.name || '';
  acceptanceMode.value = template.value.acceptance_mode || '';
  feedbackEnabled.value = Boolean(template.value.feedback_enabled);
  coverPreviewSource.value = template.value.cover_preview_source || '';
  coverPreviewBgColor.value = template.value.cover_preview_bg_color || '';
  thumbnailUrl.value = template.value.thumbnail_url || '';

  const remoteInbox = template.value.inbox_id || '';
  // Coerce — API may echo number while local is string; don't remount shell
  if (String(inboxId.value || '') !== String(remoteInbox || '')) {
    inboxId.value = remoteInbox;
  }

  const keysSame =
    JSON.stringify(portalEditableAttributeKeys.value) ===
    JSON.stringify(template.value.portal_editable_attribute_keys || []);
  const themeSame = JSON.stringify(theme.value) === JSON.stringify(remoteTheme);
  const docSame = remoteDoc === document.value;

  // Echo from our own save / page-preview upload — don't remount previews
  if (docSame && themeSame && keysSame) {
    return;
  }

  portalEditableAttributeKeys.value = [
    ...(template.value.portal_editable_attribute_keys || []),
  ];
  if (!docSame) loadFromString(remoteDoc);
  if (!themeSame) theme.value = remoteTheme;
};

watch(template, loadTemplate);

const saveTemplate = async () => {
  saving.value = true;
  try {
    flushCommitDocument();
    await store.dispatch('planningEventPortals/update', {
      id: templateId.value,
      document: document.value,
      theme: theme.value,
      name: name.value,
      acceptance_mode: acceptanceMode.value || null,
      portal_editable_attribute_keys: portalEditableAttributeKeys.value,
      feedback_enabled: feedbackEnabled.value,
      inbox_id: inboxId.value || null,
      cover_preview_source: coverPreviewSource.value || null,
      cover_preview_bg_color: coverPreviewBgColor.value || null,
    });
    await captureAndUploadPagePreview();
  } finally {
    saving.value = false;
  }
};

const captureAndUploadPagePreview = async () => {
  if (!templateId.value) return;
  try {
    const data = await store.dispatch('planningEventPortals/preview', {
      templateId: templateId.value,
      eventId: selectedEventId.value || PORTAL_PREVIEW_STUB_ID,
      document: document.value,
      theme: theme.value,
      annotate: false,
      shell: true,
      portalEditableAttributeKeys: portalEditableAttributeKeys.value,
      inboxId: inboxId.value || null,
    });
    const html = data?.html;
    if (!html) return;

    const blob = await capturePortalHtmlAsPng(html);
    // Don't await — keep save snappy; store update only touches page_preview_url
    store
      .dispatch('planningEventPortals/uploadPagePreview', {
        templateId: templateId.value,
        blob,
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.warn('Portal page preview upload failed', error);
      });
  } catch (error) {
    // Keep save successful even if preview capture fails.
    // eslint-disable-next-line no-console
    console.warn('Portal page preview capture failed', error);
  }
};

const goBack = () => {
  router.push({ name: 'settings_planning_event_portals' });
};

const openPreviewInBrowser = async () => {
  if (!templateId.value || !selectedEventId.value || openingPreview.value) {
    return;
  }
  if (!showCode.value) flushCommitDocument();
  openingPreview.value = true;
  try {
    const data = await store.dispatch('planningEventPortals/preview', {
      templateId: templateId.value,
      eventId: selectedEventId.value,
      document: document.value,
      theme: theme.value,
      annotate: false,
      shell: true,
      portalEditableAttributeKeys: portalEditableAttributeKeys.value,
      inboxId: inboxId.value || null,
    });
    const previewUrl = data?.preview_url;
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener');
      return;
    }
    const html = data?.html ?? '';
    if (!html) return;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  } finally {
    openingPreview.value = false;
  }
};

const deselect = () => {
  selectedId.value = null;
};

const selectNode = id => {
  selectedId.value = id;
  if (id) rightTab.value = 'properties';
};

const openPortalSettings = () => {
  leftTab.value = 'settings';
};

watch(document, val => {
  if (syncingFromString) return;
  if (val !== lastPushed) {
    pushState(lastPushed);
    lastPushed = val;
  }
});

const handleUndo = () => {
  flushCommitDocument();
  const prev = undo(document.value);
  if (prev !== null) {
    pause();
    loadFromString(prev);
    resume();
  }
};

const handleRedo = () => {
  flushCommitDocument();
  const next = redo(document.value);
  if (next !== null) {
    pause();
    loadFromString(next);
    resume();
  }
};

const onEditorKeyDown = e => {
  const mod = e.metaKey || e.ctrlKey;
  if (!mod) return;

  // Let CodeMirror own undo/redo while typing in the source editor
  if (e.target?.closest?.('.cm-editor')) return;

  // Let native undo work in the portal name field and similar text inputs
  const tag = e.target?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return;

  const key = e.key.toLowerCase();
  if (key === 'z') {
    e.preventDefault();
    if (e.shiftKey) handleRedo();
    else handleUndo();
    return;
  }
  // Windows/Linux redo
  if (key === 'y' && e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    handleRedo();
  }
};

watch(showCode, async (isCode, wasCode) => {
  if (isCode && !wasCode) {
    flushCommitDocument();
  } else if (!isCode && wasCode) {
    // Wait for EditorCodePanel to flush pending edits on unmount
    await nextTick();
    loadFromString(document.value);
  }
});

watch(selectedEventId, async id => {
  if (!id || isPortalPreviewStub(id)) {
    selectedEvent.value = PORTAL_PREVIEW_EVENT_STUB;
    if (!id) selectedEventId.value = PORTAL_PREVIEW_STUB_ID;
    return;
  }
  selectedEvent.value = await store.dispatch('planningEvents/show', id);
});

const insertComponentTag = tag => {
  const target = resolveInsertTarget(nodes.value, selectedId.value, tag);
  const node = makeBlock(tag);
  insertNode(nodes.value, node, target);
  nodes.value = [...nodes.value];
  selectedId.value = node.id;
  flushCommitDocument();
};

const onCanvasInsert = ({ tagName, target, preferSiblingOf }) => {
  let resolved = target;
  if (preferSiblingOf) {
    const found = findNode(nodes.value, preferSiblingOf);
    if (found) {
      resolved = {
        parentId: found.parent?.id ?? null,
        index: found.index + 1,
      };
    } else if (isLayoutInsertTag(tagName)) {
      resolved = { parentId: null, index: nodes.value.length };
    }
  }
  // Append into container when index is null/-1
  if (resolved.index == null || resolved.index === -1) {
    if (resolved.parentId) {
      const parent = findNode(nodes.value, resolved.parentId)?.node;
      resolved = {
        parentId: resolved.parentId,
        index: parent?.children?.length ?? 0,
      };
    } else {
      resolved = { parentId: null, index: nodes.value.length };
    }
  }

  // Never insert into leaf blocks (components without `{{ content }}`), otherwise
  // the new element can end up nested but not rendered ("disappears" symptom).
  if (resolved.parentId) {
    const parentFound = findNode(nodes.value, resolved.parentId);
    if (
      parentFound?.node?.kind === 'block' ||
      parentFound?.node?.kind === 'html'
    ) {
      resolved = {
        parentId: parentFound.parent?.id ?? null,
        index: parentFound.index + 1,
      };
    }
  }

  const node = makeBlock(tagName);
  insertNode(nodes.value, node, resolved);
  nodes.value = [...nodes.value];
  selectedId.value = node.id;
  flushCommitDocument();
};

const onCanvasMove = ({ id, target }) => {
  let resolved = { ...target };
  if (resolved.index == null || resolved.index === -1) {
    if (resolved.parentId) {
      const parent = findNode(nodes.value, resolved.parentId)?.node;
      resolved = {
        parentId: resolved.parentId,
        index: parent?.children?.length ?? 0,
      };
    } else {
      resolved = { parentId: null, index: nodes.value.length };
    }
  }

  // Never move into leaf blocks (components without `{{ content }}`).
  if (resolved.parentId) {
    const parentFound = findNode(nodes.value, resolved.parentId);
    if (
      parentFound?.node?.kind === 'block' ||
      parentFound?.node?.kind === 'html'
    ) {
      resolved = {
        parentId: parentFound.parent?.id ?? null,
        index: parentFound.index + 1,
      };
    }
  }

  moveNode(nodes.value, id, resolved);
  nodes.value = [...nodes.value];
  selectedId.value = id;
  flushCommitDocument();
};

const onCanvasRemove = id => {
  removeNode(nodes.value, id);
  nodes.value = [...nodes.value];
  if (selectedId.value === id) selectedId.value = null;
  flushCommitDocument();
};

const onCanvasDuplicate = id => {
  const copy = duplicateNode(nodes.value, id);
  nodes.value = [...nodes.value];
  if (copy) selectedId.value = copy.id;
  flushCommitDocument();
};

const onUpdateContent = ({ id, content }) => {
  const found = findNode(nodes.value, id);
  if (!found) return;
  found.node.content = content;
  // In-place mutate — nested reactivity updates serializeForEditor without
  // cloning the tree (avoids overlay hit-target rebuild per keystroke).
  nodesRevision.value += 1;
  scheduleCommitDocument();
};

const onUpdateSpans = ({ columnsId, spans }) => {
  const found = findNode(nodes.value, columnsId);
  if (!found || found.node.kind !== 'columns') return;
  (found.node.children || []).forEach((col, i) => {
    if (spans[i] != null) col.span = spans[i];
  });
  nodes.value = [...nodes.value];
  flushCommitDocument();
};

const onUpdateNode = ({ id, props: nextProps, content }) => {
  const found = findNode(nodes.value, id);
  if (!found) return;
  if (nextProps) {
    found.node.props = { ...found.node.props, ...nextProps };
  }
  if (content !== undefined) {
    found.node.content = content;
  }
  nodesRevision.value += 1;
  scheduleCommitDocument();
};

const onUpdateLayoutSettings = ({ columnCount, gap }) => {
  if (!selectedId.value) return;
  const found = findNode(nodes.value, selectedId.value);
  if (!found || found.node.kind !== 'columns') return;
  if (columnCount != null) applyColumnCount(found.node, columnCount);
  if (gap != null) applyColumnGap(found.node, gap);
  nodes.value = [...nodes.value];
  flushCommitDocument();
};

const eventAttributeOptions = computed(() =>
  eventAttributePickerOptions({
    attributes:
      store.getters['attributes/getAttributesByModel']('event_attribute') || [],
    categories: store.getters['customAttributeCategories/getRecords'] || [],
    t,
  })
);

onMounted(async () => {
  window.document.addEventListener('keydown', onEditorKeyDown);
  await Promise.all([
    store.dispatch('planningEventPortals/get'),
    store.dispatch('planningEventPageComponents/get'),
    store.dispatch('attributes/get'),
    store.dispatch('customAttributeCategories/get'),
    store.dispatch('inboxes/get'),
  ]);
  loadTemplate();
});

onBeforeUnmount(() => {
  flushCommitDocument();
  window.document.removeEventListener('keydown', onEditorKeyDown);
});
</script>

<template>
  <div class="fixed inset-0 z-50 flex flex-col bg-[#f4f5f7] text-n-slate-12">
    <header
      class="flex items-center justify-between h-12 px-3 bg-white border-b border-n-slate-5 shrink-0 shadow-sm"
    >
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <button
          class="flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:bg-n-slate-3 hover:text-n-slate-12 transition-colors"
          :title="$t('PLANNING.EVENT_PORTALS.EDITOR.BACK')"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <div class="w-px h-5 bg-n-slate-5" />
        <input
          v-model="name"
          type="text"
          class="reset-base bg-transparent text-sm font-medium text-n-slate-12 border-none outline-none min-w-0 max-w-[220px] truncate px-1.5 py-1 rounded hover:bg-n-slate-2 focus:bg-n-slate-2"
          :placeholder="$t('PLANNING.EVENT_PORTALS.EDITOR.NAME_PLACEHOLDER')"
        />
      </div>

      <div
        class="flex items-center p-0.5 rounded-lg bg-n-slate-3 border border-n-slate-5"
      >
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all"
          :class="
            !showCode
              ? 'bg-white text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="showCode = false"
        >
          <span class="i-lucide-pencil-ruler size-3.5" />
          Builder
        </button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all"
          :class="
            showCode
              ? 'bg-white text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="showCode = true"
        >
          <span class="i-lucide-code size-3.5" />
          Code
        </button>
      </div>

      <div class="flex items-center justify-end gap-1.5 flex-1">
        <EditorEventSelector
          v-model:selected-event-id="selectedEventId"
          :selected-event-name="selectedEvent?.name || ''"
        />

        <div class="w-px h-5 bg-n-slate-5 mx-0.5" />

        <div
          class="flex items-center p-0.5 rounded-lg bg-n-slate-3 border border-n-slate-5"
        >
          <button
            class="flex items-center justify-center size-7 rounded-md transition-all"
            :class="
              deviceMode === 'desktop'
                ? 'bg-white text-n-slate-12 shadow-sm'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            title="Desktop"
            @click="deviceMode = 'desktop'"
          >
            <span class="i-lucide-monitor size-3.5" />
          </button>
          <button
            class="flex items-center justify-center size-7 rounded-md transition-all"
            :class="
              deviceMode === 'mobile'
                ? 'bg-white text-n-slate-12 shadow-sm'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            title="Mobile"
            @click="deviceMode = 'mobile'"
          >
            <span class="i-lucide-smartphone size-3.5" />
          </button>
        </div>

        <div class="w-px h-5 bg-n-slate-5 mx-0.5" />

        <button
          class="flex items-center justify-center size-8 rounded-md transition-colors"
          :class="
            selectedEventId && !openingPreview
              ? 'text-n-slate-10 hover:bg-n-slate-3 hover:text-n-slate-12'
              : 'text-n-slate-6 cursor-not-allowed'
          "
          :disabled="!selectedEventId || openingPreview"
          :title="$t('PLANNING.EVENT_PORTALS.EDITOR.OPEN_IN_BROWSER')"
          @click="openPreviewInBrowser"
        >
          <span
            class="size-4"
            :class="
              openingPreview
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-external-link'
            "
          />
        </button>

        <div class="w-px h-5 bg-n-slate-5 mx-0.5" />

        <button
          class="flex items-center justify-center size-8 rounded-md transition-colors"
          :class="
            canUndo
              ? 'text-n-slate-10 hover:bg-n-slate-3 hover:text-n-slate-12'
              : 'text-n-slate-6 cursor-not-allowed'
          "
          :disabled="!canUndo"
          title="Undo"
          @click="handleUndo"
        >
          <span class="i-lucide-undo-2 size-4" />
        </button>
        <button
          class="flex items-center justify-center size-8 rounded-md transition-colors"
          :class="
            canRedo
              ? 'text-n-slate-10 hover:bg-n-slate-3 hover:text-n-slate-12'
              : 'text-n-slate-6 cursor-not-allowed'
          "
          :disabled="!canRedo"
          title="Redo"
          @click="handleRedo"
        >
          <span class="i-lucide-redo-2 size-4" />
        </button>

        <div class="w-px h-5 bg-n-slate-5 mx-0.5" />

        <Button
          icon="i-lucide-save"
          label="Save"
          size="sm"
          :loading="saving"
          @click="saveTemplate"
        />
      </div>
    </header>

    <div class="flex flex-1 min-h-0 overflow-hidden">
      <aside
        class="w-[280px] shrink-0 border-r border-n-slate-5 bg-white flex flex-col overflow-hidden"
      >
        <div class="px-3 pt-3 pb-2 border-b border-n-slate-5">
          <div class="flex p-0.5 rounded-lg bg-n-slate-3">
            <button
              class="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 text-xs font-medium rounded-md transition-all"
              :class="
                leftTab === 'elements'
                  ? 'bg-white text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="leftTab = 'elements'"
            >
              <span class="i-lucide-layout-grid size-3.5 shrink-0" />
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.TABS.ELEMENTS') }}
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 text-xs font-medium rounded-md transition-all"
              :class="
                leftTab === 'structure'
                  ? 'bg-white text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="leftTab = 'structure'"
            >
              <span class="i-lucide-layers size-3.5 shrink-0" />
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.TABS.STRUCTURE') }}
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 text-xs font-medium rounded-md transition-all"
              :class="
                leftTab === 'settings'
                  ? 'bg-white text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="leftTab = 'settings'"
            >
              <span class="i-lucide-settings size-3.5 shrink-0" />
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.TABS.SETTINGS') }}
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto min-h-0">
          <EditorComponentPanel
            v-if="leftTab === 'elements'"
            :components="components"
            @insert="insertComponentTag"
          />
          <EditorLayersPanel
            v-else-if="leftTab === 'structure'"
            :nodes="nodes"
            :components="components"
            :selected-id="selectedId"
            @select="selectNode"
            @insert="insertComponentTag"
            @move="onCanvasMove"
            @remove="onCanvasRemove"
          />
          <EditorPortalSettingsPanel
            v-else
            v-model:feedback-enabled="feedbackEnabled"
            v-model:inbox-id="inboxId"
            v-model:cover-preview-source="coverPreviewSource"
            v-model:cover-preview-bg-color="coverPreviewBgColor"
            v-model:thumbnail-url="thumbnailUrl"
            :template-id="templateId"
            :selected-event-id="selectedEventId"
            :theme-accent-color="theme.accent_color"
          />
        </div>
      </aside>

      <template v-if="showCode">
        <EditorCodePanel
          v-model="document"
          :components="components"
          class="flex-1"
        />
        <EditorPreviewPane
          v-model="document"
          :template-id="templateId"
          :components="components"
          :device-mode="deviceMode"
          :theme="theme"
          :portal-editable-attribute-keys="portalEditableAttributeKeys"
          :selected-event-id="selectedEventId"
          :inbox-id="inboxId"
          class="flex-1"
          @update:device-mode="m => (deviceMode = m)"
        />
      </template>

      <template v-else>
        <div class="flex-1 min-w-0 flex flex-col overflow-hidden">
          <EditorCanvas
            :pages="pages"
            :active-page-id="activePageId"
            :nodes="nodes"
            :nodes-revision="nodesRevision"
            :selected-id="selectedId"
            :components="components"
            :device-mode="deviceMode"
            :theme="theme"
            :template-id="templateId"
            :selected-event-id="selectedEventId"
            :portal-editable-attribute-keys="portalEditableAttributeKeys"
            :feedback-enabled="feedbackEnabled"
            :inbox-id="inboxId"
            :available-stages="availableStages"
            @select="selectNode"
            @deselect="deselect"
            @insert="onCanvasInsert"
            @move="onCanvasMove"
            @remove="onCanvasRemove"
            @duplicate="onCanvasDuplicate"
            @update-content="onUpdateContent"
            @update-spans="onUpdateSpans"
            @activate-page="activatePage"
            @add-page="addPage"
            @remove-page="removePage"
            @reassign-page-stage="reassignPageStage"
          />
        </div>
      </template>

      <aside
        class="w-[300px] shrink-0 border-l border-n-slate-5 bg-white flex flex-col overflow-hidden"
      >
        <div class="px-3 pt-3 pb-2 border-b border-n-slate-5">
          <div class="flex p-0.5 rounded-lg bg-n-slate-3">
            <button
              class="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 text-xs font-medium rounded-md transition-all"
              :class="
                rightTab === 'properties'
                  ? 'bg-white text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="rightTab = 'properties'"
            >
              <span class="i-lucide-sliders-horizontal size-3.5 shrink-0" />
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.TABS.PROPERTIES') }}
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 text-xs font-medium rounded-md transition-all"
              :class="
                rightTab === 'theme'
                  ? 'bg-white text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="rightTab = 'theme'"
            >
              <span class="i-lucide-palette size-3.5 shrink-0" />
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.TABS.THEME') }}
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto min-h-0">
          <EditorPropertiesPanel
            v-if="rightTab === 'properties'"
            embedded
            :selected-node="selectedNode"
            :selected-chrome-id="selectedId"
            :components="components"
            :theme="theme"
            :portal-editable-attribute-keys="portalEditableAttributeKeys"
            :event-attribute-options="eventAttributeOptions"
            @close="deselect"
            @update-node="onUpdateNode"
            @update-layout-settings="onUpdateLayoutSettings"
            @update:portal-editable-attribute-keys="
              portalEditableAttributeKeys = $event || []
            "
            @open-settings="openPortalSettings"
          />
          <EditorStylesPanel v-else v-model="theme" embedded />
        </div>
      </aside>
    </div>
  </div>
</template>
